// QuestJournal — the campaign sidebar's quest log. Scrollable, each quest expands
// to show/edit its description; players can add, rename, complete, and remove quests.
// Edits hit the REST quest CRUD and broadcast to the whole table via _push_state.
import { useState } from "react";
import { api } from "../lib/api";
import type { Quest } from "../lib/types";

export function QuestJournal({ gameId, quests }: { gameId: string; quests: Quest[] }) {
  const [openId, setOpenId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);

  const active = quests.filter((q) => !q.complete);
  const done = quests.filter((q) => q.complete);

  async function run<T>(fn: () => Promise<T>) {
    setBusy(true);
    try { await fn(); } catch { /* surfaced elsewhere */ } finally { setBusy(false); }
  }

  return (
    <div className="rail-section quests">
      <div className="rail-head">
        <span>Quests</span>
        <button className="rail-add" title="Add a quest" disabled={busy}
          onClick={() => setAdding((a) => !a)}>＋</button>
      </div>

      {adding && (
        <QuestComposer
          busy={busy}
          onCancel={() => setAdding(false)}
          onAdd={(text, desc) => run(async () => {
            await api.addQuest(gameId, text, desc);
            setAdding(false);
          })}
        />
      )}

      {active.length === 0 && !adding && (
        <div className="quest-empty">No active quests. The story will write them — or add your own.</div>
      )}

      {active.map((q) => (
        <QuestItem key={q.id} gameId={gameId} q={q} busy={busy} run={run}
          open={openId === q.id} onToggle={() => setOpenId(openId === q.id ? null : (q.id ?? null))} />
      ))}

      {done.length > 0 && (
        <div className="quest-done-head">Completed · {done.length}</div>
      )}
      {done.map((q) => (
        <QuestItem key={q.id} gameId={gameId} q={q} busy={busy} run={run}
          open={openId === q.id} onToggle={() => setOpenId(openId === q.id ? null : (q.id ?? null))} />
      ))}
    </div>
  );
}

function QuestItem({
  gameId, q, open, onToggle, busy, run,
}: {
  gameId: string;
  q: Quest;
  open: boolean;
  onToggle: () => void;
  busy: boolean;
  run: <T>(fn: () => Promise<T>) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(q.text);
  const [desc, setDesc] = useState(q.desc ?? "");
  const qid = q.id ?? "";

  return (
    <div className={`quest${q.complete ? " is-done" : ""}${open ? " is-open" : ""}`}>
      <div className="quest__row">
        <button className="quest__check" title={q.complete ? "Mark active" : "Mark complete"}
          disabled={busy}
          onClick={() => run(() => api.editQuest(gameId, qid, { complete: !q.complete }))}>
          {q.complete ? "✓" : "○"}
        </button>
        <button className="quest__title" onClick={onToggle}>
          {q.text}
          {(q.desc || open) && <span className="quest__chev">{open ? "▾" : "▸"}</span>}
        </button>
      </div>

      {open && (
        <div className="quest__body">
          {editing ? (
            <>
              <input className="field field--sm" value={text} onChange={(e) => setText(e.target.value)}
                placeholder="Quest title" />
              <textarea className="field field--sm quest__ta" value={desc} rows={4}
                onChange={(e) => setDesc(e.target.value)} placeholder="Describe the objective, leads, stakes…" />
              <div className="quest__actions">
                <button className="btn btn--sm" disabled={busy}
                  onClick={() => run(async () => { await api.editQuest(gameId, qid, { text, desc }); setEditing(false); })}>Save</button>
                <button className="btn btn--ghost btn--sm" onClick={() => { setText(q.text); setDesc(q.desc ?? ""); setEditing(false); }}>Cancel</button>
              </div>
            </>
          ) : (
            <>
              {q.desc ? <p className="quest__desc">{q.desc}</p>
                      : <p className="quest__desc quest__desc--empty">No description yet.</p>}
              <div className="quest__actions">
                <button className="btn btn--ghost btn--sm" onClick={() => setEditing(true)}>✎ Edit</button>
                <button className="btn btn--ghost btn--sm quest__del" disabled={busy}
                  onClick={() => run(() => api.deleteQuest(gameId, qid))}>Remove</button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function QuestComposer({
  onAdd, onCancel, busy,
}: {
  onAdd: (text: string, desc: string) => void;
  onCancel: () => void;
  busy: boolean;
}) {
  const [text, setText] = useState("");
  const [desc, setDesc] = useState("");
  return (
    <div className="quest quest--new is-open">
      <div className="quest__body">
        <input className="field field--sm" autoFocus value={text} placeholder="Quest title"
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && text.trim()) onAdd(text.trim(), desc); }} />
        <textarea className="field field--sm quest__ta" value={desc} rows={3}
          onChange={(e) => setDesc(e.target.value)} placeholder="Description (optional)" />
        <div className="quest__actions">
          <button className="btn btn--sm" disabled={busy || !text.trim()}
            onClick={() => onAdd(text.trim(), desc)}>Add quest</button>
          <button className="btn btn--ghost btn--sm" onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>
  );
}
